import React from "react";
import { getMasterVolume, onMasterVolume } from "../../../_visor/utils/masterVolume";
import {
  createAudioSyncState,
  driveAudioSync,
  resetAudioSync,
  seekAudioSync,
} from "../../../common/audio_sync.es6";
import {
  isRouted,
  openTimelineAudio,
  sharedAudioContext,
  unlockAudioGraph,
  unrouteAudio,
} from "../../../common/audio_graph.es6";
import {
  scenePlayback,
  sceneSeek,
  sceneTimeBus,
  secToFrame,
} from "../../../common/scene_clock.es6";
import { resolveBoxTiming } from "../../../common/resolveBoxTiming.es6";
import { isMediaSkipped, pokeMediaGate, registerMediaHolder } from "../../../common/media_gate.es6";
import { reloadSource } from "../../../common/media_holders.es6";
import { audioCueManual } from "../../../common/audio_cue_manual.es6";
require("../AudioCue.scss");

import imagePlay from "./../../../dist/images/play.svg";
import imagePause from "./../../../dist/images/pause.svg";
import AudioCuePlayerBar from "../AudioCuePlayerBar";

/* eslint-disable react/prop-types */

// Element events after which a waiting scene may go on.
const GATE_EVENTS = ["canplay", "canplaythrough", "seeked", "loadeddata"];
// Element events the player view's time and play state follow.
const VIEW_EVENTS = ["loadedmetadata", "timeupdate", "play", "pause", "ended"];

export default class AudioCueComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      playing: false,
      deltaTime: 0,
      currentTime: 0,
      duration: 0,
      audioPaused: true,
      muted: false,
    };
    this._trimEndReached = false;
    this._audioEnded = false;
    // A scene cue the learner pressed: the scene no longer starts, stops or moves it, and the
    // scene does not wait for it. An explicit scene seek, a replay or a new visit hands it back.
    this._learnerOwned = false;
    // The Visor's pause stopped a cue the learner was playing: its resume plays it again.
    this._pausedByScene = false;
    this._sync = createAudioSyncState();
    this._seekId = null;

    // Sound-list cues (deprecated): kept only so older content still plays;
    // to be removed. New sounds go to the scene narration instead.
    if (props.state.soundList != null && props.state.soundList.length > 0) {
      this.audio = new Audio();
    } else {
      this._adoptAudio(this._openAudio(props));
    }
  }

  // The window the scene plays this cue in, or null for a cue the scene does
  // not start (learner-started, or a sound list with its own clock). A
  // single-file autoplay cue from before the timeline has no window of its
  // own: it plays from where the Visor shows it (resolveBoxTiming).
  _sceneTimeline(props = this.props) {
    const list = props.state?.soundList;
    if (Array.isArray(list) && list.length > 0) return null; // runs its own clock
    const p = props.props || {};
    const box = p.boxes?.[p.id];
    if (audioCueManual(props.state, box)) return null;
    const tl = box?.timeline;
    if (typeof tl?.startFrame === "number") return tl;
    const legacyAnims = window.IdealStudio?.State?.animationsById?.[p.id] || [];
    const { startFrame } = resolveBoxTiming(box, legacyAnims);
    return { ...(tl || {}), startFrame: typeof startFrame === "number" ? startFrame : 0 };
  }

  // Not waiting for the learner's press (see audioCueManual).
  _autoStarts(props = this.props) {
    const p = props.props || {};
    return !audioCueManual(props.state, p.boxes?.[p.id]);
  }

  // Cues the scene starts play through the shared audio context; a
  // learner-started one keeps a plain element.
  _openAudio(props) {
    if (!this._sceneTimeline(props)) return new Audio(props.state.url);
    // The scene preload already holds the file: play it from memory instead
    // of loading it again (a seek into the clip creates this cue on the spot).
    const held = typeof window.__visorAudioObjectUrl === "function";
    const src = (held && window.__visorAudioObjectUrl(props.state.url)) || props.state.url;
    return openTimelineAudio(src, this._onAudioFallback);
  }

  // The source may not be routed through the audio context (no CORS headers):
  // carry on with a plain element. Nothing has played yet, and the start
  // intent lives in this._sync, so it still starts where it was meant to.
  _onAudioFallback = (plain, failed) => {
    if (this._unmounted || failed !== this.audio) return; // replaced meanwhile
    failed.removeEventListener("ended", this._onAudioEnded);
    this._adoptAudio(plain);
    // A learner-started cue was already told to play: start it from the
    // in-point once it can seek, if that request still stands by then.
    const start = () => {
      if (this._unmounted || this.audio !== plain) return;
      if (!this.state.playing || this.props.props.prewarm) return;
      const trimStartSec = this._getTrimStartSec();
      if (plain.currentTime < trimStartSec) plain.currentTime = trimStartSec;
      plain.play().catch(() => {});
    };
    if (!this.state.playing) return;
    if (plain.readyState >= 1) start();
    else plain.addEventListener("loadedmetadata", start, { once: true });
  };

  _adoptAudio(el) {
    this.audio = el;
    // Mounted shortly before its start (VisorCanvasSli): load the file now.
    el.preload = "auto";
    // Detached Audio() — the player bar's DOM volume sweep can't reach it,
    // so the master volume is folded in here and on every master change.
    el.volume = this._boxVolume() * getMasterVolume();
    el.addEventListener("ended", this._onAudioEnded);
    GATE_EVENTS.forEach((t) => el.addEventListener(t, pokeMediaGate));
    VIEW_EVENTS.forEach((t) => el.addEventListener(t, this._syncPlayerTime));
    this._sync.deferRate = isRouted(el);
  }

  _onAudioEnded = () => {
    this._audioEnded = true;
    this._trimEndReached = true;
    this.setState({ playing: false });
  };

  _dropAudio() {
    const el = this.audio;
    if (!el) return;
    el.removeEventListener("ended", this._onAudioEnded);
    GATE_EVENTS.forEach((t) => el.removeEventListener(t, pokeMediaGate));
    VIEW_EVENTS.forEach((t) => el.removeEventListener(t, this._syncPlayerTime));
    el.pause();
    unrouteAudio(el);
    el.src = "";
    resetAudioSync(this._sync);
  }

  _boxVolume() {
    const v = this.props ? this.props.state.volume : null;
    return v != null && v !== undefined ? parseInt(v) / 100 : 1;
  }

  _getTrimStartSec() {
    const box = this.props.props?.boxes?.[this.props.props?.id];
    const t = box?.timeline?.trimStart;
    return typeof t === "number" && t > 0 ? t / 30 : 0;
  }

  _getTrimEndSec() {
    const box = this.props.props?.boxes?.[this.props.props?.id];
    const t = box?.timeline?.trimEnd;
    return typeof t === "number" ? t / 30 : null;
  }
  playPause() {
    // Mounted early to load: not on stage yet.
    if (this.props.props.prewarm) return;
    const sceneCue = !!this._sceneTimeline();
    if (sceneCue) this._learnerOwned = true;
    // A pre-timeline cue drove the legacy animations; one on the timeline leaves them to the scene.
    const box = this.props.props.boxes?.[this.props.props.id];
    const drivesAnimation = !sceneCue && typeof box?.timeline?.startFrame !== "number";
    this._pausedByScene = false;
    // A start still pending (e.g. waiting for metadata) counts as playing.
    if (this.state.playing || !this.audio.paused) {
      if (drivesAnimation) this.props.props.animationPause();
      this.audio.pause();
      this.setState({ playing: false });
      return;
    }
    unlockAudioGraph(); // in the press: a routed cue needs a running context
    this.setState({ playing: true }, () => {
      this.props.props.setPlayingAudioUrlInContained(this.props.props.id);
      if (drivesAnimation) this.props.props.animationPlay();
      // trimStart'tan başla (eğer başa dönmek gerekiyorsa)
      const trimStartSec = this._getTrimStartSec();
      const trimEndSec = this._getTrimEndSec();
      const isPastEnd = trimEndSec !== null && this.audio.currentTime >= trimEndSec;
      const isBeforeStart = this.audio.currentTime < trimStartSec;
      if (isBeforeStart || isPastEnd) {
        this.audio.currentTime = trimStartSec;
        this._trimEndReached = false;
        this._audioEnded = false;
      }
      this.audio.play().catch(() => {});
    });
  }

  // Back under the scene (explicit seek, replay, a new visit).
  _releaseToScene() {
    this._learnerOwned = false;
    this._pausedByScene = false;
  }

  _syncPlayerTime = () => {
    const el = this.audio;
    if (!el || this._unmounted) return;
    this.setState({
      currentTime: el.currentTime || 0,
      duration: isFinite(el.duration) ? el.duration : 0,
      audioPaused: el.paused,
    });
  };

  // The player view's track: a moment within the trimmed window.
  _playerSeek(sec) {
    if (this._sceneTimeline()) this._learnerOwned = true;
    this.audio.currentTime = this._getTrimStartSec() + sec;
    this._trimEndReached = false;
    this._audioEnded = false;
    this._syncPlayerTime();
  }

  _playerToggleMute() {
    this.setState({ muted: !this.state.muted }, () => {
      this.audio.muted = this.state.muted;
    });
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    if (this.audio)
      VIEW_EVENTS.forEach((t) => this.audio.removeEventListener(t, this._syncPlayerTime));
    if (this._offMasterVolume) {
      this._offMasterVolume();
      this._offMasterVolume = null;
    }
    if (this._onSceneEnded) {
      document.removeEventListener("aidea-scene-ended", this._onSceneEnded);
      this._onSceneEnded = null;
    }
    this._unmounted = true;
    if (this._offSceneClock) this._offSceneClock();
    if (this._offFrame) this._offFrame();
    if (this._offMediaGate) this._offMediaGate();
    this._dropAudio();
  }

  componentDidMount() {
    const p0 = this.props.props || {};
    const tl0 = this._sceneTimeline();
    if (tl0) {
      this._onClock = true;
      this._clockRunning = scenePlayback.running && scenePlayback.sceneId === p0.currentView;
      this._offSceneClock = scenePlayback.register(this._onSceneClock);
      const cue = this;
      this._offMediaGate = registerMediaHolder({
        id: this._gateId(),
        kind: "audio",
        sceneId: () => this.props.props.currentView,
        due: (sec) => this._gateDue(sec),
        ready: () => this._canPlay(3),
        settled: () => this._canPlay(4),
        blockedBy: () => (this._hasData(3) && !this._contextRuns() ? "context" : null),
        failed: () => !!(this.audio && this.audio.error),
        // Unmounted (its window passed): the element's source is gone, so fetch the file by its URL.
        retry: () =>
          !this._unmounted && this.audio && this.audio.getAttribute("src")
            ? this.audio.load()
            : reloadSource("audio", this.props.state.url),
        get startSec() {
          const tl = cue._sceneTimeline();
          return tl ? tl.startFrame / 30 : undefined;
        },
      });
      // Created by a seek into its clip: take the position now, while the
      // scene still waits, so the seek is done when the clock starts.
      this._applySceneSeek(
        tl0,
        typeof tl0.trimStart === "number" ? tl0.trimStart / 30 : 0,
        typeof tl0.trimEnd === "number" ? tl0.trimEnd / 30 : null
      );
      if (!p0.recorderMode) {
        this._offFrame = sceneTimeBus.subscribeFrame(this._onCommittedFrame);
        this._onCommittedFrame(); // may mount inside its window
      }
    }
    this._offMasterVolume = onMasterVolume((m) => {
      this.audio.volume = this._boxVolume() * m;
    });

    // Scene end stops every in-scene sound, manual or autoplay (VisorApp's
    // aidea-scene-ended broadcast). The detached Audio() is unreachable from
    // any DOM sweep, and the componentDidUpdate range gate only covers cues
    // WITH a timeline — a learner-started cue would keep playing under the
    // scene-end CTA without this.
    this._onSceneEnded = (evt) => {
      const sceneId = evt && evt.detail && evt.detail.sceneId;
      if (sceneId && sceneId !== this.props.props?.currentView) return;
      if (!this.state.playing && (!this.audio || this.audio.paused)) return;
      this.setState({ playing: false }, () => {
        if (this.audio) this.audio.pause();
      });
    };
    document.addEventListener("aidea-scene-ended", this._onSceneEnded);
    // Sound-list cues are not adopted in the constructor.
    if (this.props.state.soundList != null && this.props.state.soundList.length > 0) {
      this.audio.addEventListener("ended", this._onAudioEnded);
      VIEW_EVENTS.forEach((t) => this.audio.addEventListener(t, this._syncPlayerTime));
    }

    this.interval = setInterval(() => {
      if (this.state.playing) {
        this.setState({ deltaTime: ++this.state.deltaTime });
      }
      // trimEnd kontrolü — state.playing'den bağımsız, audio çalışıyorsa her zaman kontrol et
      if (!this.audio.paused) {
        const trimEndSec = this._getTrimEndSec();
        const trimStartSec = this._getTrimStartSec();
        if (trimEndSec !== null && this.audio.currentTime >= trimEndSec) {
          this._trimEndReached = true;
          this.audio.pause();
          this.audio.currentTime = trimStartSec;
          resetAudioSync(this._sync);
          this.setState({ playing: false });
        }
      }
    }, 100);

    if (this._autoStarts()) {
      // A cue the scene drives starts with the scene clock — no second start here.
      const hasTimeline = !!tl0;
      // All scenes mount at app open — an unguarded mount-play fired cues over
      // the preload screen / hidden-tab deferral (audio under a paused stage).
      // Only play when the scene is truly running; otherwise the
      // visorPlaying-rise in componentWillReceiveProps starts it later.
      const p = this.props.props || {};
      const sceneRunning =
        p.show && p.startButtonClicked && p.preloadCompleted && p.visorPlaying && !p.prewarm;
      if (!hasTimeline && sceneRunning) {
        this._playFromInPoint();
      }
    }
  }

  // Starts a no-timeline autoplay cue from its in-point (deferred metadata safe).
  _playFromInPoint() {
    const trimStartSec = this._getTrimStartSec();
    const doPlay = () => {
      this.setState({ playing: true }, () => {
        this.props.props.setPlayingAudioUrlInContained(this.props.props.id);
        if (trimStartSec > 0) this.audio.currentTime = trimStartSec;
        this.audio.play().catch(() => {});
      });
    };
    if (trimStartSec > 0 && this.audio.readyState < 1) {
      this.audio.addEventListener("loadedmetadata", doPlay, { once: true });
    } else {
      doPlay();
    }
  }
  componentDidUpdate(prevProps) {
    // Scene replay (resetTriggered pulse): rewind to the in-point so the next
    // start — a manual click or the timeline window — begins from the top,
    // not from where the scene-end stop left the audio.
    if (!prevProps.props.resetTriggered && this.props.props.resetTriggered) {
      this._audioEnded = false;
      this._trimEndReached = false;
      this._releaseToScene();
      const trimStartSec = this._getTrimStartSec();
      this.setState({ playing: false, deltaTime: 0 }, () => {
        if (!this.audio) return;
        this.audio.pause();
        resetAudioSync(this._sync);
        try {
          this.audio.currentTime = trimStartSec;
        } catch (e) {
          /* metadata not loaded yet — playback starts from 0 anyway */
        }
      });
      return;
    }

    const pluginState = this.props.state;
    const props = this.props.props;
    const prevPropsInner = prevProps.props;
    // soundList kullanan AudioCue'lar bu kontrolden muaf
    if (pluginState.soundList && pluginState.soundList.length > 0) return;
    // Gizli sahnelerde ses çalma — show=false ise hemen çık
    if (!props.show) return;

    // Live playback follows the committed frame (_onCommittedFrame).
    const frameChanged =
      props.recorderMode && props.visorFrameTime !== prevPropsInner.visorFrameTime;
    const playingChanged =
      props.prewarm !== prevPropsInner.prewarm ||
      props.visorPlaying !== prevPropsInner.visorPlaying ||
      props.videoBuffering !== prevPropsInner.videoBuffering ||
      props.playbackRate !== prevPropsInner.playbackRate ||
      props.visorSeekTo !== prevPropsInner.visorSeekTo;
    if (!frameChanged && !playingChanged) return;
    // Live: the committed frame, not the (possibly stale) frame prop.
    if (!props.recorderMode) {
      this._onCommittedFrame();
      return;
    }

    const sceneSec =
      sceneTimeBus.sceneId === props.currentView
        ? sceneTimeBus.sec
        : (props.visorFrameTime || 0) / 30;
    this._driveTimeline(props.visorFrameTime || 0, sceneSec);
  }

  _onCommittedFrame = () => {
    const props = this.props.props;
    const cv = props.currentView;
    const sceneId = Array.isArray(cv) ? cv[cv.length - 1] : cv;
    if (props.recorderMode || !props.show || sceneTimeBus.frameSceneId !== sceneId) return;
    const frame = sceneTimeBus.frame;
    this._driveTimeline(frame, sceneTimeBus.sceneId === sceneId ? sceneTimeBus.sec : frame / 30);
  };

  // The scene clock started or stopped. Acting here, not on the React update
  // that follows, starts a ready cue together with the clock.
  _onSceneClock = (running, sec, sceneId) => {
    const props = this.props.props;
    const mine = sceneId === props.currentView;
    this._clockRunning = running && mine;
    if (!mine || !props.show) return;
    this._driveTimeline(secToFrame(sec), sec);
  };

  // A scene seek is an explicit command, seen once — also by a cue created
  // while or after it happened (boxes mount when their range starts). The
  // record ends when the scene restarts, so a replay starts from the in-point.
  _applySceneSeek(tl, trimStartSec, trimEndSec) {
    const FPS = 30;
    if (sceneSeek.sceneId !== this.props.props.currentView || sceneSeek.id === this._seekId) return;
    this._seekId = sceneSeek.id;
    this._releaseToScene();
    const seekFrame = sceneSeek.sec * FPS;
    const mediaSec = trimStartSec + (seekFrame - tl.startFrame) / FPS;
    const intoClip =
      seekFrame >= tl.startFrame &&
      (typeof tl.endFrame !== "number" || seekFrame <= tl.endFrame) &&
      (trimEndSec === null || mediaSec < trimEndSec);
    if (intoClip) {
      this._trimEndReached = false;
      this._audioEnded = false;
      seekAudioSync(this._sync, this.audio, mediaSec);
    } else {
      // Before or past the clip: reaching it later is a start from the in-point.
      resetAudioSync(this._sync);
    }
  }

  // Scene end (authored duration marker): the playhead freezes there while
  // the scene may keep waiting (e.g. waitForCorrectAction), but this audio
  // runs on wall clock — treat the scene end as out-of-range so it stops
  // with the scene. Same gate as VisorCanvasSli's video controller.
  // `byTime`: the gate asks about a frame React has not drawn yet.
  _inRange(tl, currentFrame, { byTime = false } = {}) {
    const props = this.props.props;
    const sceneEndFrame = window.IdealStudio?.State?.navItemsById?.[props.currentView]?.duration;
    return (
      (byTime || !props.prewarm) &&
      currentFrame >= tl.startFrame &&
      (typeof tl.endFrame !== "number" || currentFrame <= tl.endFrame) &&
      (!(sceneEndFrame > 0) || currentFrame < sceneEndFrame)
    );
  }

  _gateId() {
    return `cue:${this.props.props.id}`;
  }

  _gateDue(at) {
    const props = this.props.props;
    const tl = this._sceneTimeline();
    if (!tl || !props.show || this._learnerOwned || this._trimEndReached || this._audioEnded) {
      return false;
    }
    const sec =
      typeof at === "number"
        ? at
        : sceneTimeBus.sceneId === props.currentView
          ? sceneTimeBus.sec
          : (props.visorFrameTime || 0) / 30;
    return this._inRange(tl, secToFrame(sec), { byTime: typeof at === "number" });
  }

  _canPlay(readyState) {
    return this._hasData(readyState) && this._contextRuns();
  }

  _hasData(readyState) {
    const el = this.audio;
    return !!el && !el.seeking && el.readyState >= readyState;
  }

  _contextRuns() {
    if (!this.audio || !isRouted(this.audio)) return true;
    const ctx = sharedAudioContext();
    return !ctx || ctx.state === "running";
  }

  // Starts, keeps or stops the cue for scene frame `currentFrame` (`sceneSec`
  // is the exact time). Called from React updates and by the scene clock.
  _driveTimeline(currentFrame, sceneSec) {
    const props = this.props.props;
    // parentBox başka bir box'a ait olabilir — her zaman kendi id'si üzerinden oku
    const tl = this._sceneTimeline();
    if (!tl) return;

    const FPS = 30;
    const inRange = this._inRange(tl, currentFrame);
    const trimStartSec = typeof tl.trimStart === "number" ? tl.trimStart / FPS : 0;
    const trimEndSec = typeof tl.trimEnd === "number" ? tl.trimEnd / FPS : null;

    // Range dışına çıkınca flag'leri sıfırla — sonraki play hazır olsun
    if (!inRange) {
      this._trimEndReached = false;
      this._audioEnded = false;
    }

    this._applySceneSeek(tl, trimStartSec, trimEndSec);
    if (this._learnerOwned) return;

    // Registered with the scene clock: it says whether the scene runs, also
    // before React's own flag has caught up.
    const playing = this._onClock ? this._clockRunning : props.visorPlaying;
    if (inRange && playing && !props.videoBuffering && !isMediaSkipped(this._gateId())) {
      if (trimEndSec !== null && this.audio.currentTime >= trimEndSec) {
        this._trimEndReached = true;
        if (!this.audio.paused) {
          this.audio.pause();
          this.audio.currentTime = trimStartSec;
          resetAudioSync(this._sync);
        }
      } else if (!this._trimEndReached && !this._audioEnded) {
        const mediaTargetSec = trimStartSec + (sceneSec - tl.startFrame / FPS);
        driveAudioSync(
          this._sync,
          this.audio,
          mediaTargetSec,
          props.playbackRate || 1,
          trimEndSec,
          trimStartSec
        );
      }
    } else {
      if (!this.audio.paused) {
        this.audio.pause();
        if (!inRange) {
          this.audio.currentTime = trimStartSec;
          resetAudioSync(this._sync);
        }
      }
    }
  }

  render() {
    let { props, state } = this.props;
    // A scene cue plays without state.playing: follow the element too.
    const isPlaying = this.state.playing || !this.state.audioPaused;
    let imagePlayPause = isPlaying ? imagePause : imagePlay;

    if (this.state.playing) {
      if (state.soundList == null || state.soundList.length == 0) {
        this.audio.onended = () => {
          this.setState({ playing: false }, () => {
            this.audio.pause();
          });
        };
      }
    }

    let totalDuration = 0;

    if (state.soundList != null && state.soundList.length > 0) {
      totalDuration =
        state.soundList.reduce(function (prev, curr) {
          return prev.endTime > curr.endTime ? prev : curr;
        }).endTime -
        state.soundList.reduce(function (prev, curr) {
          return prev.startTime < curr.startTime ? prev : curr;
        }).startTime;
    }

    if (
      this.interval != null &&
      this.state.deltaTime > 0 &&
      totalDuration > 0 &&
      this.state.deltaTime >= totalDuration
    ) {
      this.setState(
        {
          deltaTime: 0,
          playing: false,
        },
        () => {
          this.audio.pause();
          this.audio.currentTime = 0;
        }
      );
    }

    if (
      this.state.playing &&
      state.soundList != null &&
      state.soundList.length > 0 &&
      state.soundList.find((sound) => sound.startTime == this.state.deltaTime) != null
    ) {
      let sound = state.soundList.find((sound) => sound.startTime == this.state.deltaTime);
      this.audio.src = sound.file;
      this.audio.volume = sound.volumeLevel * getMasterVolume();
      this.audio.currentTime = 0;

      this.props.props.setPlayingAudioUrlInContained(this.props.props.id);
      this.audio.play();
    } else if (
      this.state.playing &&
      state.soundList != null &&
      state.soundList.length > 0 &&
      state.soundList.find((sound) => sound.endTime == this.state.deltaTime) != null
    ) {
      this.audio.src = "";
      this.audio.currentTime = 0;
    }

    // Mounted early to load: only the Audio object lives, nothing is drawn.
    // (The children set visibility/pointer-events themselves, so hiding the
    // box around them would not hide them.)
    if (props.prewarm) return null;

    if (state.playerView) {
      // Time readout is relative to the trimmed in/out window.
      const trimStartSec = this._getTrimStartSec();
      const trimEndSec = this._getTrimEndSec();
      const endSec =
        trimEndSec !== null && (!this.state.duration || trimEndSec < this.state.duration)
          ? trimEndSec
          : this.state.duration;
      const windowSec = Math.max(0, endSec - trimStartSec);
      const elapsedSec = Math.min(windowSec, Math.max(0, this.state.currentTime - trimStartSec));
      return (
        <div className={"audioCueConatiner"} style={{ width: "100%", height: "100%" }}>
          <AudioCuePlayerBar
            playing={isPlaying}
            currentTime={elapsedSec}
            duration={windowSec}
            muted={this.state.muted}
            onPlayPause={this.playPause.bind(this)}
            onSeek={this._playerSeek.bind(this)}
            onToggleMute={this._playerToggleMute.bind(this)}
          />
        </div>
      );
    }

    let useImage = state.useImage;
    return (
      <div className={"audioCueConatiner"} style={{ width: "100%", height: "100%" }}>
        <button
          onClick={this.playPause.bind(this)}
          style={{ height: "100%", width: "100%", pointerEvents: "initial" }}
          className={"draggableImage"}
        >
          <div
            className={"colorBackground"}
            style={{
              height: "100%",
              width: "100%",
              pointerEvents: "initial",
              backgroundColor: state.colorCue,
              visibility: useImage ? "hidden" : "visible",
            }}
          />
          <img
            className="basicImageClass"
            style={{
              overflow: "hidden",
              width: state.allowDeformed ? "100%" : "100%",
              height: state.allowDeformed ? "100%" : "100%",
              visibility: useImage ? "visible" : "hidden",
              position: "absolute",
            }}
            src={state.icon}
          />
          {state.hideAnimation ? null : (
            <div className={"loader1"} id="bars" style={{ position: "absolute", display: "block" }}>
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barUp playing" : "barUp"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />

              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
              <div
                className={isPlaying ? "barDown playing" : "barDown"}
                style={{ animationPlayState: isPlaying ? "running" : "paused" }}
              />
            </div>
          )}
          <img
            className={state.hideAnimation ? "playButtonCentered" : "playButton"}
            src={imagePlayPause}
          />
        </button>
      </div>
    );
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.state.url !== nextProps.state.url) {
      const usesList = nextProps.state.soundList != null && nextProps.state.soundList.length > 0;
      if (usesList) {
        this.audio.setAttribute("src", nextProps.state.url);
      } else {
        // A new source may or may not be routable: start over with a new element.
        this._dropAudio();
        // "Ended" belonged to the old file.
        this._audioEnded = false;
        this._trimEndReached = false;
        this._releaseToScene();
        this._adoptAudio(this._openAudio(nextProps));
      }
    }

    if (this.props.state.volume !== nextProps.state.volume) {
      this.audio.volume = (parseInt(nextProps.state.volume) / 100) * getMasterVolume();
    }

    const showNow = this.props.props.show;
    const showNext = nextProps.props.show;

    if (!showNext && showNow) {
      // Sahne gizleniyor — sesi durdur
      this._releaseToScene();
      this.setState({ playing: false, deltaTime: -1 }, () => {
        this.audio.pause();
      });
    } else if (showNext && !showNow) {
      // Sahne tekrar görünür hale geliyor — flag'leri sıfırla ve gerekirse çal
      this._audioEnded = false;
      this._trimEndReached = false;
      this._releaseToScene();

      const nextBox = nextProps.props.boxes?.[nextProps.props.id];
      const tl = nextBox?.timeline;
      const trimStartSec =
        typeof tl?.trimStart === "number" && tl.trimStart > 0 ? tl.trimStart / 30 : 0;

      // load() currentTime'ı sıfırlar — sadece yeni src varsa load et
      if (this.props.state.url !== nextProps.state.url) {
        this.audio.load();
        // load sonrası canplaythrough beklenmeden currentTime set edilebilir
        this.audio.addEventListener(
          "loadedmetadata",
          () => {
            this.audio.currentTime = trimStartSec;
          },
          { once: true }
        );
      } else {
        // Aynı src — doğrudan seek et
        this.audio.currentTime = trimStartSec;
      }

      const hasTimeline = !!this._sceneTimeline(nextProps);
      if (this._autoStarts(nextProps) && !hasTimeline) {
        this.setState({ playing: true }, () => {
          if (this.props.state.volume !== nextProps.state.volume) {
            this.audio.volume = (parseInt(nextProps.state.volume) / 100) * getMasterVolume();
          }
          this.props.props.setPlayingAudioUrlInContained(this.props.props.id);
          this.audio.play();
        });
      }
    }

    if (!this.props.props.visorPlaying && nextProps.props.visorPlaying) {
      // Deferred autoplay: the mount-time play is gated on the scene actually
      // running, so a cue whose scene start was pending (preload screen /
      // hidden-tab deferral) begins here, on the real scene start. The
      // currentTime guard keeps mid-scene RESUMES from replaying a cue that
      // already ran (or that the learner stopped).
      const hasTimeline = !!this._sceneTimeline(nextProps);
      const trimStartSec = this._getTrimStartSec();
      if (
        this._autoStarts(nextProps) &&
        !hasTimeline &&
        nextProps.props.show &&
        !this.state.playing &&
        !this._audioEnded &&
        this.audio.currentTime <= trimStartSec + 0.05
      ) {
        this._playFromInPoint();
      }
      if (this._learnerOwned && this._pausedByScene && nextProps.props.show) {
        this._pausedByScene = false;
        this.setState({ playing: true });
        this.audio.play().catch(() => {});
      }
    } else if (this.props.props.visorPlaying && !nextProps.props.visorPlaying) {
      if (this._learnerOwned && !this.audio.paused) this._pausedByScene = true;
      this.setState({ playing: false }, () => {
        this.audio.pause();
      });
    }

    if (
      nextProps.props.playingAudioInContained !== null &&
      nextProps.props.playingAudioInContained !== undefined
    ) {
      if (this.props.props.id !== nextProps.props.playingAudioInContained) {
        //başka bir ses oynamaya başladından dur
        this.setState({ playing: false }, () => {
          this.audio.pause();
        });
      }
    }
  }
}

/* eslint-enable react/prop-types */
